import { useMutation, useQueryClient } from "@tanstack/react-query";

import { errorHandler } from "#app/monitoring/errorHandler";
import { showErrorToast } from "#app/notifications";
import { invalidateQuoteRequestLinesDebounced } from "#modules/quoteRequestLines/infra/invalidateQuoteRequestLinesDebounced";
import {
  getQuoteRequestLineQueryData,
  setQuoteRequestLineQueryData,
} from "#modules/quoteRequestLines/infra/quoteRequestLine.query";
import {
  UpdateQuoteRequestLineQuantity,
  updateQuoteRequestLineQuantity,
} from "#modules/quoteRequestLines/infra/updateQuoteRequestLineQuantity.api";
import { queryKeys } from "#shared/queryKeys/queryKeys";

export const useUpdateQuoteRequestLineQuantity = (
  options: {
    onSuccess?: (qrls: UpdateQuoteRequestLineQuantity["updatedQuoteRequestLines"]) => void;
    onError?: (err: Error) => void;
    onMutate?: () => void;
  } = {},
) => {
  const queryClient = useQueryClient();

  const { mutate, ...rest } = useMutation({
    mutationFn: updateQuoteRequestLineQuantity,
    onMutate: () => {
      options.onMutate?.();
    },
    onSuccess: (response, { payload }) => {
      const { quantity } = payload;

      response.updatedQuoteRequestLines.forEach((quoteRequestLine) => {
        setQuoteRequestLineQueryData(queryClient, quoteRequestLine.id, (previous) =>
          previous ? { ...previous, quantity } : previous,
        );

        invalidateQuoteRequestLinesDebounced({
          queryClient,
          quoteRequestLineId: quoteRequestLine.id,
        });
      });

      const quoteRequestId = response.updatedQuoteRequestLines[0]?.quoteRequestId;
      if (quoteRequestId) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.quoteRequests.weight(quoteRequestId),
          exact: true,
        });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.quoteRequests.transport(quoteRequestId),
          exact: true,
        });
      }

      options.onSuccess?.(response.updatedQuoteRequestLines);
    },
    onError: (err: Error) => {
      showErrorToast({ title: err.message });
      errorHandler(err);

      options.onError?.(err);
    },
  });

  const updateQuoteRequestLineQuantityIfChanged: typeof mutate = (variables, mutateOptions) => {
    const currentQuantity = getQuoteRequestLineQueryData(queryClient, variables.id)?.quantity;
    if (currentQuantity === variables.payload.quantity) {
      return;
    }

    mutate(variables, mutateOptions);
  };

  return { updateQuoteRequestLineQuantity: updateQuoteRequestLineQuantityIfChanged, ...rest };
};
